// services/project.service.js
import httpClient from './httpClient';
import TokenService from './token.service';

/**
 * Project Service
 * Maneja todas las operaciones relacionadas con Projects
 * Base URL configured via REACT_APP_AUTH_API_URL or REACT_APP_API_BASE_URL
 */

// Usar cliente HTTP centralizado
const authClient = httpClient;

const ProjectService = {
  /**
   * Crear nuevo proyecto
   * POST /projects
   * Body: { name, description, account_id?, organization_id? }
   */
  async createProject(projectData) {
    try {
      // Preparar el payload completo
      const payload = {
        name: projectData.name,
        description: projectData.description || ''
      };
      
      // Incluir account_id solo si existe y no es undefined
      if (projectData.account_id && projectData.account_id !== undefined) {
        payload.account_id = projectData.account_id;
      }
      
      // Incluir organization_id solo si existe y no es undefined
      if (projectData.organization_id && projectData.organization_id !== undefined) {
        payload.organization_id = projectData.organization_id;
      }
      
      // Verificar que tenemos account_id antes de enviar
      if (!payload.account_id) {
        return {
          success: false,
          error: ['account_id is required for project creation']
        };
      }
      
      const response = await authClient.post('/projects', payload);
      
      // Estructura: response.data.body contiene project_id, name, description, etc.
      return {
        success: true,
        data: {
          id: response.data.body.project_id,
          project_id: response.data.body.project_id,
          name: response.data.body.name,
          description: response.data.body.description,
          owner_id: response.data.body.owner_id,
          account_id: response.data.body.account_id,
          organization_id: response.data.body.organization_id,
          status: response.data.body.status,
          created_at: response.data.body.created_at
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || ['Failed to create project']
      };
    }
  },

  /**
   * Obtener detalles de un proyecto
   * GET /projects/{project_id}
   */
  async getProject(projectId) {
    try {
      const response = await authClient.get(`/projects/${projectId}`);
      return {
        success: true,
        data: response.data.body.project
      };
    } catch (error) {
      console.error('Error fetching project:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to fetch project']
      };
    }
  },

  /**
   * Actualizar proyecto
   * PUT /projects/{project_id}
   */
  async updateProject(projectId, updates) {
    try {
      const response = await authClient.put(`/projects/${projectId}`, updates);
      return {
        success: true,
        data: response.data.body.project
      };
    } catch (error) {
      console.error('Error updating project:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to update project']
      };
    }
  },

  /**
   * Eliminar proyecto (soft delete)
   * DELETE /projects/{project_id}
   */
  async deleteProject(projectId) {
    try {
      const response = await authClient.delete(`/projects/${projectId}`);
      return {
        success: true,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error deleting project:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to delete project']
      };
    }
  },

  /**
   * Listar proyectos personales
   * GET /projects?account_id={account_id}&page=1&page_size=20
   */
  async listPersonalProjects({ accountId, page = 1, pageSize = 10 } = {}) {
    try {
      // Si no se proporciona accountId, intentar obtenerlo del usuario actual
      if (!accountId) {
        const userData = TokenService.getUserData();
        accountId = userData?.account_id || userData?.default_account_id;
      }
      
      if (!accountId) {
        console.error('No account_id available for listing projects');
        return {
          success: false,
          error: ['No account_id provided'],
          data: [],
          totalCount: 0
        };
      }
      
      const response = await authClient.get('/projects', {
        params: {
          account_id: accountId,
          page,
          page_size: pageSize
        }
      });
      
      // Validar que la respuesta tenga la estructura esperada
      const projects = response.data?.body?.projects;
      if (!Array.isArray(projects)) {
        return {
          success: false,
          error: ['Invalid projects response format'],
          data: [],
          totalCount: 0
        };
      }
      
      return {
        success: true,
        data: projects.map(p => ({
          id: p.project_id,
          project_id: p.project_id,
          name: p.name,
          description: p.description,
          status: p.status,
          owner_id: p.owner_id,
          account_id: p.account_id,
          organization_id: p.organization_id,
          created_at: p.created_at,
          updated_at: p.updated_at
        })),
        totalCount: response.data?.body?.total_count || 0,
        page: response.data?.body?.page || page,
        pageSize: response.data?.body?.page_size || pageSize
      };
    } catch (error) {
      console.error('Error listing personal projects:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || ['Failed to list personal projects'],
        data: [],
        totalCount: 0
      };
    }
  },

  /**
   * Listar proyectos de organización
   * GET /projects?account_id={account_id}&page=1&page_size=20
   * Para organizaciones, usar el account_id de la organización
   */
  async listOrganizationProjects(organizationId, { page = 1, pageSize = 10 } = {}) {
    try {
      // Para organizaciones, necesitamos obtener el account_id de la organización
      // Por ahora, asumimos que organizationId es el account_id de la organización
      const response = await authClient.get('/projects', {
        params: {
          account_id: organizationId,
          page,
          page_size: pageSize
        }
      });
      
      const projects = response.data?.body?.projects;
      if (!Array.isArray(projects)) {
        return {
          success: false,
          error: ['Invalid projects response format'],
          data: [],
          totalCount: 0
        };
      }
      
      return {
        success: true,
        data: projects.map(p => ({
          id: p.project_id,
          project_id: p.project_id,
          name: p.name,
          description: p.description,
          status: p.status,
          owner_id: p.owner_id,
          account_id: p.account_id,
          organization_id: p.organization_id,
          created_at: p.created_at,
          updated_at: p.updated_at
        })),
        totalCount: response.data?.body?.total_count || 0
      };
    } catch (error) {
      console.error('Error listing organization projects:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || ['Failed to list organization projects'],
        data: [],
        totalCount: 0
      };
    }
  },

  /**
   * Listar proyectos de una cuenta específica
   * GET /projects?account_id={account_id}&page=1&page_size=20
   */
  async listAccountProjects(accountId, { page = 1, pageSize = 10 } = {}) {
    try {
      const response = await authClient.get('/projects', {
        params: {
          account_id: accountId,
          page,
          page_size: pageSize
        }
      });
      
      const projects = response.data?.body?.projects;
      if (!Array.isArray(projects)) {
        return {
          success: false,
          error: ['Invalid projects response format'],
          data: [],
          totalCount: 0
        };
      }
      
      return {
        success: true,
        data: projects.map(p => ({
          id: p.project_id,
          project_id: p.project_id,
          name: p.name,
          description: p.description,
          status: p.status,
          owner_id: p.owner_id,
          account_id: p.account_id,
          organization_id: p.organization_id,
          created_at: p.created_at,
          updated_at: p.updated_at
        })),
        totalCount: response.data?.body?.total_count || 0
      };
    } catch (error) {
      console.error('Error listing account projects:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || ['Failed to list account projects'],
        data: [],
        totalCount: 0
      };
    }
  },

  /**
   * Listar todos los proyectos del usuario
   * GET /projects?page=1&page_size=20
   */
  async listAllProjects({ page = 1, pageSize = 20 } = {}) {
    try {
      const response = await authClient.get('/projects', {
        params: {
          page,
          page_size: pageSize
        }
      });
      return {
        success: true,
        data: response.data.body.projects,
        totalCount: response.data.body.total_count
      };
    } catch (error) {
      console.error('Error listing all projects:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to list all projects'],
        data: [],
        totalCount: 0
      };
    }
  }
};

export default ProjectService;
